import { BranchNodeData, WorkflowEdge, WorkflowNode } from "lib/types/workflow"

import {
  BRANCH_GAP,
  BRANCH_Y_STEP,
  CONDITION_Y_STEP,
  LAYOUT_Y_STEP,
  NODE_W,
  SIBLING_GAP
} from "../constants"
import {
  BRANCH,
  branchCaseHandle,
  CONDITION,
  HANDLE_BRANCH_OTHERWISE,
  HANDLE_OTHERWISE,
  HANDLE_OUTPUT,
  HANDLE_THEN,
  TRIGGER
} from "../../nodeKind"

type Child = { target: string; handle: string }

const TRIGGER_START_X = 250

export const computeLayout = (
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): Map<string, { x: number; y: number }> => {
  const positions = new Map<string, { x: number; y: number }>()

  const childrenMap = new Map<string, Child[]>()
  for (const edge of edges) {
    const list = childrenMap.get(edge.source) ?? []
    list.push({ target: edge.target, handle: edge.sourceHandle ?? HANDLE_OUTPUT })
    childrenMap.set(edge.source, list)
  }

  const trigger = nodes.find((n) => n.type === TRIGGER)
  if (!trigger) return positions

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const widthCache = new Map<string, number>()

  const conditionBranches = (
    nodeId: string
  ): { thenChildren: Child[]; otherwiseChildren: Child[] } => {
    const children = childrenMap.get(nodeId) ?? []
    return {
      thenChildren: children.filter((c) => c.handle === HANDLE_THEN),
      otherwiseChildren: children.filter((c) => c.handle === HANDLE_OTHERWISE)
    }
  }

  /** Group BRANCH children by outcome (one row per case + the otherwise
   *  fallback). Outcome order matches the order of cases on the node so the
   *  layout mirrors the source-handle order rendered by BranchNode. */
  const branchOutcomes = (node: WorkflowNode): Child[][] => {
    const data = node.data as BranchNodeData
    const children = childrenMap.get(node.id) ?? []
    const rows: Child[][] = data.cases.map((c) => {
      const handle = branchCaseHandle(c.id)
      return children.filter((ch) => ch.handle === handle)
    })
    rows.push(children.filter((c) => c.handle === HANDLE_BRANCH_OTHERWISE))
    return rows
  }

  const rowWidth = (siblings: Child[]): number => {
    if (siblings.length === 0) return 0
    let total = 0
    for (const c of siblings) total += measure(c.target)
    return total + SIBLING_GAP * (siblings.length - 1)
  }

  const measure = (nodeId: string): number => {
    const cached = widthCache.get(nodeId)
    if (cached !== undefined) return cached

    const node = nodeMap.get(nodeId)
    const children = childrenMap.get(nodeId) ?? []

    let width: number
    if (node?.type === CONDITION) {
      const { thenChildren, otherwiseChildren } = conditionBranches(nodeId)
      const thenWidth = Math.max(NODE_W, rowWidth(thenChildren))
      const otherwiseWidth = Math.max(NODE_W, rowWidth(otherwiseChildren))
      width = Math.max(NODE_W, thenWidth + BRANCH_GAP + otherwiseWidth)
    } else if (node?.type === BRANCH) {
      const outcomes = branchOutcomes(node)
      const outcomeWidths = outcomes.map((row) => Math.max(NODE_W, rowWidth(row)))
      const total =
        outcomeWidths.reduce((sum, w) => sum + w, 0) +
        BRANCH_GAP * Math.max(0, outcomes.length - 1)
      width = Math.max(NODE_W, total)
    } else {
      width = Math.max(NODE_W, rowWidth(children))
    }

    widthCache.set(nodeId, width)
    return width
  }

  const placeRow = (siblings: Child[], startLeft: number, y: number) => {
    let cursor = startLeft
    for (const child of siblings) {
      place(child.target, cursor, y)
      cursor += measure(child.target) + SIBLING_GAP
    }
  }

  const place = (nodeId: string, leftX: number, y: number) => {
    if (positions.has(nodeId)) return

    const node = nodeMap.get(nodeId)
    const width = measure(nodeId)
    positions.set(nodeId, { x: leftX + (width - NODE_W) / 2, y })

    if (node?.type === CONDITION) {
      const { thenChildren, otherwiseChildren } = conditionBranches(nodeId)
      const branchY = y + CONDITION_Y_STEP
      const thenWidth = Math.max(NODE_W, rowWidth(thenChildren))
      // Each branch row sits inside its allotted width — empty branches still
      // reserve NODE_W so the condition stays centred between both sides.
      placeRow(
        thenChildren,
        leftX + (thenWidth - rowWidth(thenChildren)) / 2,
        branchY
      )
      const otherwiseLeft = leftX + thenWidth + BRANCH_GAP
      const otherwiseWidth = Math.max(NODE_W, rowWidth(otherwiseChildren))
      placeRow(
        otherwiseChildren,
        otherwiseLeft + (otherwiseWidth - rowWidth(otherwiseChildren)) / 2,
        branchY
      )
    } else if (node?.type === BRANCH) {
      const outcomes = branchOutcomes(node)
      const branchY = y + BRANCH_Y_STEP
      let cursor = leftX
      for (const row of outcomes) {
        const rowW = Math.max(NODE_W, rowWidth(row))
        placeRow(row, cursor + (rowW - rowWidth(row)) / 2, branchY)
        cursor += rowW + BRANCH_GAP
      }
    } else {
      const children = childrenMap.get(nodeId) ?? []
      const childRowWidth = rowWidth(children)
      placeRow(
        children,
        leftX + (width - childRowWidth) / 2,
        y + LAYOUT_Y_STEP
      )
    }
  }

  const triggerWidth = measure(trigger.id)
  const triggerLeftX = TRIGGER_START_X - (triggerWidth - NODE_W) / 2
  place(trigger.id, triggerLeftX, 0)

  let maxY = 0
  for (const pos of positions.values()) {
    if (pos.y > maxY) maxY = pos.y
  }
  for (const node of nodes) {
    if (!positions.has(node.id)) {
      maxY += LAYOUT_Y_STEP
      positions.set(node.id, { x: TRIGGER_START_X, y: maxY })
    }
  }

  return positions
}
